import { useCallback } from 'react';
import U2000AutoCompleteInput from '../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import { U2000FormStateEnumEditMode } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U3129ProjectSearchPanel } from '../../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells';
import type { TFunction } from 'i18next';
import { NS } from '../../../U3141';
import type { U3141FindProjectItem, U3141FindRequestedLotItem, U3141MasterLotChain } from '../../../U3141Service';
import { useU3141FindProjectHandlers } from '../../../U3141UseFindProjectHandlers';
import { useU3141FindRequestedLotHandlers } from '../../../U3141UseFindRequestedLotHandlers';

interface U3141MasterLotChainBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3141MasterLotChain>;
  t: TFunction;
}

function U3141MasterLotChainBody({ formStateHandlers, t }: U3141MasterLotChainBodyProps) {
  const findProjectHandlers = useU3141FindProjectHandlers();
  const findRequestedLotHandlers = useU3141FindRequestedLotHandlers();
  const editMode = formStateHandlers.getFormEditMode();
  const isAddMode = editMode === U2000FormStateEnumEditMode.ADD;
  const isEditMode = editMode === U2000FormStateEnumEditMode.EDIT;

  const formData = formStateHandlers.getData();
  const hasProjectSelected = Boolean(formData?.projectNumber?.trim());

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      return findProjectHandlers.search(criterias);
    },
    [findProjectHandlers],
  );

  const onSearchRequestedLot = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const projectNumber = formStateHandlers.getData()?.projectNumber?.trim();
      if (!projectNumber) return [];
      return findRequestedLotHandlers.search([{ fieldName: 'projectNumber', value: projectNumber }, ...criterias]);
    },
    [findRequestedLotHandlers, formStateHandlers],
  );

  const extractProject = useCallback((result: U3141FindProjectItem | null): { fieldName: string; value: unknown }[] => {
    if (result) {
      return [
        { fieldName: 'selectedProject', value: result },
        { fieldName: 'projectNumber', value: result.projetNumber ?? '' },
        { fieldName: 'projectDescription', value: result.projetName ?? '' },
        { fieldName: 'selectedRequestedLot', value: null },
        { fieldName: 'requestedLot', value: '' },
      ];
    }
    return [
      { fieldName: 'selectedProject', value: null },
      { fieldName: 'projectNumber', value: '' },
      { fieldName: 'projectDescription', value: '' },
      { fieldName: 'selectedRequestedLot', value: null },
      { fieldName: 'requestedLot', value: '' },
    ];
  }, []);

  const extractRequestedLot = useCallback((result: U3141FindRequestedLotItem | null): { fieldName: string; value: unknown }[] => {
    if (result?.requestedLot) {
      const lot = result.requestedLot;
      return [
        { fieldName: 'selectedRequestedLot', value: { id: lot, requestedLot: lot } },
        { fieldName: 'requestedLot', value: lot },
      ];
    }
    return [
      { fieldName: 'selectedRequestedLot', value: null },
      { fieldName: 'requestedLot', value: '' },
    ];
  }, []);

  const projectNumberLabel = t('lblMasterLotchain.ProjectNumber', { ns: NS });
  const projectDescriptionLabel = t('lblMasterLotchain.ProjectDescription', { ns: NS });
  const requestedLotLabel = t('lblMasterLotchain.RequestedLot', { ns: NS });

  return (
    <div className="grid gap-2 w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {isAddMode ? (
          <U2000AutoCompleteInput
            pageSize={25}
            formStateHandlers={formStateHandlers}
            fieldName="selectedProject"
            mainFieldName="projetNumber"
            entityLink="U3121"
            label={projectNumberLabel}
            required
            onSearch={onSearchProject}
            displayFn={(item) => {
              const p = item as U3141FindProjectItem;
              return p ? (p.projetNumber ?? p.id) : '';
            }}
            overrideExtractDataFn={(item) => extractProject(item as U3141FindProjectItem | null)}
            basicResultHeight={300}
            resultLabels={[]}
            resultFields={['projetNumber', 'projetName']}
            minCarLaunchSearch={3}
            customSearchPanelHeight={500}
            panelWidth={800}
            dropdownWidth={400}
            renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns={NS} />}
          />
        ) : (
          <U2000InputText
            fieldName="projectNumber"
            label={projectNumberLabel}
            formStateHandlers={formStateHandlers}
            readonly={isEditMode}
          />
        )}
        <U2000InputText
          fieldName="projectDescription"
          label={projectDescriptionLabel}
          formStateHandlers={formStateHandlers}
          readonly={isAddMode || isEditMode}
          required={isAddMode}
        />
      </div>

      {isAddMode ? (
        <div className="space-y-1">
          <U2000AutoCompleteInput
            pageSize={25}
            formStateHandlers={formStateHandlers}
            fieldName="selectedRequestedLot"
            mainFieldName="requestedLot"
            label={requestedLotLabel}
            required
            readonly={!hasProjectSelected}
            onSearch={onSearchRequestedLot}
            displayFn={(item) => (item as U3141FindRequestedLotItem)?.requestedLot ?? ''}
            overrideExtractDataFn={(item) => extractRequestedLot(item as U3141FindRequestedLotItem | null)}
            basicResultHeight={200}
            resultLabels={[requestedLotLabel]}
            resultFields={['requestedLot']}
            minCarLaunchSearch={0}
            dropdownWidth={400}
          />
          {!hasProjectSelected ? (
            <p className="text-xs text-gray-500">{t('hintRequestedLotSelectProjectFirst', { ns: NS })}</p>
          ) : null}
        </div>
      ) : (
        <U2000InputText
          fieldName="requestedLot"
          label={requestedLotLabel}
          formStateHandlers={formStateHandlers}
          readonly={isEditMode}
          required={!isEditMode}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <U2000InputText
          fieldName="createdByUser"
          label={t('lblMasterLotchain.CreatedByUser', { ns: NS })}
          formStateHandlers={formStateHandlers}
          readonly={true}
        />
        <U2000InputText
          fieldName="createdAt"
          label={t('lblMasterLotchain.CreatedAt', { ns: NS })}
          formStateHandlers={formStateHandlers}
          readonly={true}
        />
      </div>
    </div>
  );
}

export default U3141MasterLotChainBody;
